import { useEffect, useRef } from "react";
import { Capacitor } from '@capacitor/core';
import { AudioSession, InterruptionTypes } from '../lib/audioSession';

// Global flags so different components share the same state
// (kept on window to survive re-renders and match your widget side flags)
if (typeof window !== "undefined") {
  window._audioUnlocked = window._audioUnlocked ?? false;           // set true after first user gesture
  window._vapiReinitInProgress = window._vapiReinitInProgress ?? false;
  window._vapiCallManuallyEnded = window._vapiCallManuallyEnded ?? false;
  window._vapiHasEverStartedCall = window._vapiHasEverStartedCall ?? false; // set true after first user start
}

let reinitCooldown; // debounce timer shared across mounts

export function markCallManuallyEnded(value = true) {
  if (typeof window !== "undefined") {
    window._vapiCallManuallyEnded = !!value;
  }
}

/**
 * Vapi-optimized audio recovery:
 * - Do NOT create AudioContext until first user gesture (prevents autoplay warning).
 * - Do NOT attempt reconnects until:
 *    a) audio is unlocked (user gesture), and
 *    b) user has started at least one call before.
 * - Uses @capgo/capacitor-audio-session to listen for iOS interruptions and route changes.
 */
export function useAudioRecovery(reinitVoiceAgent) {
  const audioContextRef = useRef(null);
  const deviceChangeTimer = useRef(null);

  const toggleAudioOutput = async () => {
    if (Capacitor.getPlatform() === 'web') {
      console.log("[AudioRecovery] Audio toggle not supported on web");
      return;
    }
    try {
      // Check current outputs to decide what to toggle to
      const result = await AudioSession.currentOutputs();
      const outputs = result.outputs || [];
      const currentOutput = outputs.length > 0 ? outputs[0] : 'unknown';
      console.log(`[AudioRecovery] Current output: ${currentOutput}`);

      if (currentOutput === 'builtin-speaker') {
        console.log("[AudioRecovery] Switching to default (receiver/bluetooth)...");
        await AudioSession.overrideOutput({ type: 'default' });
      } else {
        console.log("[AudioRecovery] Switching to speaker...");
        await AudioSession.overrideOutput({ type: 'builtin-speaker' });
      }
    } catch (err) {
      console.error("[AudioRecovery] Error toggling audio:", err);
    }
  };

  useEffect(() => {
    // Create/resume AudioContext only AFTER first user gesture
    const createContextOnFirstGesture = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        // On some platforms it starts as "suspended" until this very click
      }
      window._audioUnlocked = true;
      console.log("[AudioRecovery] Audio unlocked by user gesture");
      window.removeEventListener("click", createContextOnFirstGesture, true);
    };

    // If not unlocked yet, attach a capture-phase click just once
    if (!window._audioUnlocked) {
      window.addEventListener("click", createContextOnFirstGesture, { once: true, capture: true });
      console.log("[AudioRecovery] Waiting for user gesture to create/resume AudioContext");
    }

    const resumeAudio = async (source = "unknown") => {
      try {
        if (!window._audioUnlocked || !audioContextRef.current) return;
        if (audioContextRef.current.state === "closed") {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
          console.log("[AudioRecovery] AudioContext recreated after closure");
        }
        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
          console.log(`[AudioRecovery] AudioContext resumed (source: ${source})`);
        }
      } catch (err) {
        console.warn("[AudioRecovery] Error resuming AudioContext:", err);
      }
    };

    // Helper to force the OS to recognize audio intent by briefly requesting mic access
    const kickAudioSystem = async () => {
      try {
        console.log("[AudioRecovery] Kicking audio system with dummy stream...");
        // Requesting a stream can force iOS to switch the audio session category back to PlayAndRecord
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Immediately stop it - we just needed the "kick"
        stream.getTracks().forEach(track => track.stop());
        console.log("[AudioRecovery] Audio system kicked successfully.");
      } catch (err) {
        console.warn("[AudioRecovery] Failed to kick audio system:", err);
      }
    };

    const safeReinit = async (source = "unknown") => {
      // Never auto-start a first-time call:
      if (!window._audioUnlocked) return;                 // user hasn't interacted yet
      if (!window._vapiHasEverStartedCall) return;       // user hasn't started any call yet
      if (window._vapiCallManuallyEnded) return;         // user explicitly ended
      if (window._vapiReinitInProgress) return;          // debounce

      try {
        window._vapiReinitInProgress = true;
        clearTimeout(reinitCooldown);
        console.log(`[AudioRecovery] Reinitializing Vapi session (source: ${source})`);
        
        // Let the widget implement the end/start logic, passing the kick function
        // so the widget can decide WHEN to kick (e.g. after ending the previous call)
        await reinitVoiceAgent?.(kickAudioSystem);
      } catch (err) {
        console.error("[AudioRecovery] Error reinitializing Vapi session:", err);
      } finally {
        reinitCooldown = setTimeout(() => (window._vapiReinitInProgress = false), 1500);
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      
      console.log("[AudioRecovery] App became visible. Waiting 300ms for Bluetooth/Audio routes to settle...");
      
      // Add delay to allow iOS audio routing (Bluetooth) to settle before we try to take over
      setTimeout(async () => {
        await resumeAudio("visibility");
        await safeReinit("visibility");
      }, 300);
    };

    const handleFocus = async () => {
      // Focus fires on initial page load — guards above prevent unwanted autostart
      await resumeAudio("focus");
      await safeReinit("focus");
    };

    const handleDeviceChange = async () => {
      // Debounce bursts of devicechange events
      clearTimeout(deviceChangeTimer.current);
      deviceChangeTimer.current = setTimeout(async () => {
        await resumeAudio("devicechange");
        await safeReinit("devicechange");
      }, 750);
    };

    // Capacitor AudioSession Listeners
    let interruptionListener;
    let routeChangeListener;

    const setupAudioSessionListeners = async () => {
      if (Capacitor.getPlatform() === 'web') return;

      // Allow disabling the plugin via env var (to fix speaker pops on some devices)
      if (import.meta.env.VITE_DISABLE_IOS_AUDIO_PLUGIN === 'true') {
        console.log("[AudioRecovery] iOS Audio Plugin disabled via env var");
        return;
      }

      try {
        interruptionListener = await AudioSession.addListener('interruption', async (type) => {
          console.log(`[AudioRecovery] Audio interruption: ${type}`);
          if (type === InterruptionTypes.ENDED) {
             // Interruption ended (e.g. call finished), kick the system to regain control
             await resumeAudio('interruption-ended');
             await safeReinit('interruption-ended');
          }
        });

        routeChangeListener = await AudioSession.addListener('routeChanged', async (reason) => {
          console.log(`[AudioRecovery] Audio route changed: ${reason}`);
          // If route changed (e.g. bluetooth connected/disconnected), we might need to re-kick
          await resumeAudio(`route-change-${reason}`);
          await safeReinit(`route-change-${reason}`);
        });
        
        console.log("[AudioRecovery] AudioSession listeners attached");
      } catch (err) {
        // Plugin might not be available on web or android if not configured
        console.warn("[AudioRecovery] Failed to setup AudioSession listeners (plugin might be missing):", err);
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    navigator.mediaDevices?.addEventListener?.("devicechange", handleDeviceChange);
    console.log("[AudioRecovery] Setting up Audio recovery listeners...");
    setupAudioSessionListeners();

    return () => {
      window.removeEventListener("click", createContextOnFirstGesture, { capture: true });
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      navigator.mediaDevices?.removeEventListener?.("devicechange", handleDeviceChange);
      // Remove Capacitor listeners
      interruptionListener?.remove();
      routeChangeListener?.remove();
      // Do NOT close the AudioContext here — keeping it available avoids extra prompts
    };
  }, [reinitVoiceAgent]);
}
